import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  Card, Collapse, Table, Button, Space, Tag, Input, Drawer, 
  message, Select, Form, Radio, Upload, Popconfirm, 
  DatePicker, Modal, Descriptions, Image, 
  Typography, Tabs, TimePicker, InputNumber, Tooltip, Badge
} from 'antd';
import { 
  PlusOutlined, DeleteOutlined, ReloadOutlined, 
  UploadOutlined, FilterOutlined, SearchOutlined, 
  ClearOutlined, EyeOutlined,
  SendOutlined, ClockCircleOutlined, PlayCircleOutlined,
  PauseCircleOutlined, HistoryOutlined, CalendarOutlined
} from '@ant-design/icons';
import { broadcastAPI, classAPI, schoolAPI, gradeManagementAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import moment from 'moment';
import { useWorkbenchContext } from '../../contexts/WorkbenchContext';
import { WorkbenchDrawer } from '../schools/WorkbenchFormPanel';
const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea, Search } = Input;
const { RangePicker } = DatePicker;
const { TabPane } = Tabs;
const WEEKDAY_OPTIONS = [
  { value: 1, label: '星期一', shortLabel: '周一' },
  { value: 2, label: '星期二', shortLabel: '周二' },
  { value: 3, label: '星期三', shortLabel: '周三' },
  { value: 4, label: '星期四', shortLabel: '周四' },
  { value: 5, label: '星期五', shortLabel: '周五' },
  { value: 6, label: '星期六', shortLabel: '周六' },
  { value: 7, label: '星期日', shortLabel: '周日' }
];
const WEEKDAY_SHORT_MAP = WEEKDAY_OPTIONS.reduce((result, item) => {
  result[item.value] = item.shortLabel;
  return result;
}, {});
const normalizeWeekdayValues = (weekdayValue) => {
  if (Array.isArray(weekdayValue)) {
    return [...new Set(
      weekdayValue
        .map((value) => parseInt(value, 10))
        .filter((value) => value >= 1 && value <= 7)
    )].sort((left, right) => left - right);
  }
  if (typeof weekdayValue === 'string') {
    return normalizeWeekdayValues(weekdayValue.split(','));
  }
  if (weekdayValue === undefined || weekdayValue === null || weekdayValue === '') {
    return [];
  }
  return normalizeWeekdayValues([weekdayValue]);
};
const formatWeekdayText = (weekdayValue, labelMap = WEEKDAY_SHORT_MAP) => (
  normalizeWeekdayValues(weekdayValue)
    .map((value) => labelMap[value])
    .filter(Boolean)
    .join('\u3001')
);
const BroadcastList = () => {
  const workbench = useWorkbenchContext();
  const alive = useRef(true), sending = useRef(false);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  // 获取用户信息和权限
  const { user, isSuperAdmin, isSchoolAdmin } = useAuth();
  const [entryParams] = useSearchParams();
  const entrySchool = entryParams.get('schoolId'), entryAction = entryParams.get('action');
  const handledEntry = useRef('');
  const canSendSchool = Boolean(isSuperAdmin() || isSchoolAdmin());
  // ========== 公共状态 ==========
  const [activeTab, setActiveTab] = useState('history'); // 'history' 或 'scheduled'
  const [loading, setLoading] = useState(false);
  const [classes, setClasses] = useState([]);
  const [schools, setSchools] = useState([]);
  // ========== 即时广播历史相关状态 ==========
  const [messageHistory, setMessageHistory] = useState([]);
  const [senders, setSenders] = useState([]);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [filterDrawerVisible, setFilterDrawerVisible] = useState(false);
  const [formData, setFormData] = useState({
    type: 'text',
    content: '',
    displayMode: 'banner',
    targetClass: null,
    targetSchool: workbench?.schoolId || null,
    targetGrade: null,
    sendMode: 'class'
  });
  const [fileList, setFileList] = useState([]);
  const [targetGrades, setTargetGrades] = useState([]);
  const [gradesLoading, setGradesLoading] = useState(false);
  const [filters, setFilters] = useState({
    schoolId: null,
    classId: null,
    type: null,
    dateRange: null,
    contentQuery: '',
    senderId: null
  });
  const [historyPagination, setHistoryPagination] = useState({
    current: 1,
    pageSize: 100,
    total: 0
  });
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [detailVisible, setDetailVisible] = useState(false);
  const [currentMessage, setCurrentMessage] = useState(null);
  // ========== 定时广播相关状态 ==========
  const [scheduledBroadcasts, setScheduledBroadcasts] = useState([]);
  const [scheduledDrawerVisible, setScheduledDrawerVisible] = useState(false);
  const [scheduledFormData, setScheduledFormData] = useState({
    title: '',
    content: '',
    broadcast_type: 'text',
    display_mode: 'popup',
    target_class_id: null,
    schedule_type: 'weekly', // 'once', 'daily', 'weekly', 'monthly'
    schedule_time: null,
    schedule_weekday: [],
    schedule_date: null,
    specific_date: null
  });
  const [scheduledFileList, setScheduledFileList] = useState([]);
  const [selectedScheduledKeys, setSelectedScheduledKeys] = useState([]);
  const [logsVisible, setLogsVisible] = useState(false);
  const [currentLogs, setCurrentLogs] = useState([]);
  const [currentSchedule, setCurrentSchedule] = useState(null);
  useEffect(() => {
    fetchClasses();
    fetchSchools();
    fetchSenders();
    fetchMessageHistory(1, 100);
  }, []);
  useEffect(() => {
    if (activeTab === 'scheduled') {
      fetchScheduledBroadcasts();
    }
  }, [activeTab]);
  useEffect(() => {
    const key = `${entrySchool}:${entryAction}`;
    if (entryAction !== 'send' || !entrySchool || !canSendSchool || handledEntry.current === key) return;
    const school = schools.find(school => String(school.id) === entrySchool);
    if (!school) return;
    handledEntry.current = key;
    setFormData(current => ({ ...current, targetSchool: school.id, targetGrade: null, sendMode: 'school' }));
    setDrawerVisible(true);
  }, [entrySchool, entryAction, schools, canSendSchool]);
  useEffect(() => {
    let active = true;
    setTargetGrades([]);
    if (formData.sendMode !== 'grade' || !formData.targetSchool) { setGradesLoading(false); return; }
    setGradesLoading(true);
    gradeManagementAPI.context({ schoolId: String(formData.targetSchool) }).then((response) => {
      if (active) setTargetGrades(response.data.data.scopes.map((scope) => scope.grade));
    }).catch(() => { if (active) message.error('加载学校年级失败，请重新选择学校'); })
      .finally(() => { if (active) setGradesLoading(false); });
    return () => { active = false; };
  }, [formData.sendMode, formData.targetSchool]);
  // ========== 公共方法 ==========
  // 获取班级列表
  const fetchClasses = async () => {
    try {
      setLoading(true);
      const response = await classAPI.getAll({
        pageSize: 200, schoolId: workbench?.schoolId
      });
      if (response.data.success) {
        setClasses(response.data.data);
      }
    } catch (error) {
      console.error('获取班级列表失败:', error);
      message.error('获取班级列表失败');
    } finally {
      setLoading(false);
    }
  };
  // 获取学校列表
  const fetchSchools = async () => {
    try {
      const response = workbench ? await schoolAPI.getById(workbench.schoolId) : await schoolAPI.getAll();
      if (response.data.success) {
        const school = response.data.data;
        setSchools(workbench ? (String(school.id) === String(workbench.schoolId) ? [school] : []) : school);
      }
    } catch (error) {
      console.error('获取学校列表失败:', error);
      message.error('获取学校列表失败');
    }
  };
  // 获取发送者列表
  const fetchSenders = async () => {
    try {
      const response = await broadcastAPI.getSenders();
      if (response.data.success) {
        setSenders(response.data.data);
      }
    } catch (error) {
      console.error('获取发送者列表失败:', error);
      message.error('获取发送者列表失败');
    }
  };
  // ========== 即时广播历史相关方法 ==========
  // 获取消息历史
  const fetchMessageHistory = async (page = 1, pageSize = 100, filterParams = {}) => {
    setLoading(true);
    try {
      const params = {
        page,
        pageSize,
        limit: pageSize,
        ...filterParams, ...(workbench ? { schoolId: workbench.schoolId } : {})
      };
      const response = await broadcastAPI.getMessageHistory(params);
      if (response.data.success) {
        setMessageHistory(response.data.data);
        if (response.data.pagination) {
          setHistoryPagination({
            current: response.data.pagination.current,
            pageSize: response.data.pagination.pageSize,
            total: response.data.pagination.total
          });
        }
      }
    } catch (error) {
      console.error('获取消息历史失败:', error);
      message.error('获取消息历史失败');
    } finally {
      setLoading(false);
    }
  };
  // 处理快速搜索
  const handleQuickSearch = (value) => {
    setSearchLoading(true);
    const newFilters = { ...filters, contentQuery: value };
    setFilters(newFilters);
    fetchMessageHistory(1, historyPagination.pageSize, { ...prepareFilterParams(), contentQuery: value })
      .finally(() => setSearchLoading(false));
  };
  // 处理表格行选择变化
  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };
  // 批量删除选中的消息
  const handleBatchDelete = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请至少选择一条消息进行删除');
      return;
    }
    setDeleteLoading(true);
    try {
      const promises = selectedRowKeys.map(id => broadcastAPI.deleteMessage(id));
      await Promise.all(promises);
      message.success(`成功删除 ${selectedRowKeys.length} 条广播消息`);
      setSelectedRowKeys([]);
      fetchMessageHistory(1, historyPagination.pageSize, prepareFilterParams());
    } catch (error) {
      console.error('批量删除消息失败:', error);
      message.error('批量删除消息失败');
    } finally {
      setDeleteLoading(false);
    }
  };
  // 准备过滤参数
  const prepareFilterParams = () => {
    const params = {};
    if (filters.schoolId) params.schoolId = filters.schoolId;
    if (filters.classId) params.classId = filters.classId;
    if (filters.type) params.type = filters.type;
    if (filters.senderId) params.senderId = filters.senderId;
    if (filters.contentQuery) params.contentQuery = filters.contentQuery;
    if (filters.dateRange && filters.dateRange.length === 2) {
      params.startDate = filters.dateRange[0].format('YYYY-MM-DD');
      params.endDate = filters.dateRange[1].format('YYYY-MM-DD');
    }
    return params;
  };
  // 应用过滤器
  const applyFilters = () => {
    const params = prepareFilterParams();
    fetchMessageHistory(1, historyPagination.pageSize, params);
    setFilterDrawerVisible(false);
  };
  // 清空过滤器
  const clearFilters = () => {
    setFilters({
      schoolId: null,
      classId: null,
      type: null,
      dateRange: null,
      contentQuery: '',
      senderId: null
    });
    fetchMessageHistory(1, 100);
  };
  // 显示消息详情
  const showMessageDetail = (record) => {
    setCurrentMessage(record);
    setDetailVisible(true);
  };
  // 处理表单字段变化
  const handleFormChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };
  // 处理文件变化
  const handleFileChange = ({ fileList: newFileList }) => {
    setFileList(newFileList);
  };
  // 发送广播
  const handleSendBroadcast = async () => {
    if (!alive.current || sending.current) return;
    try {
      if (formData.type === 'text' && !formData.content) {
        message.error('请输入广播内容');
        return;
      }
      if (formData.type !== 'text' && fileList.length === 0) {
        message.error('请上传文件');
        return;
      }
      if (formData.sendMode === 'class' && !formData.targetClass) {
        message.error('请选择目标班级');
        return;
      }
      const targetSchool = workbench?.schoolId || formData.targetSchool;
      if (['school', 'grade'].includes(formData.sendMode) && !targetSchool) {
        message.error('请选择目标学校');
        return;
      }
      if (formData.sendMode === 'grade' && !formData.targetGrade) {
        message.error('请选择年级');
        return;
      }
      sending.current = true;
      setLoading(true);
      let contentToSend = formData.content;
      if (formData.type !== 'text' && fileList.length > 0) {
        const uploadFormData = new FormData();
        uploadFormData.append('file', fileList[0].originFileObj);
        const uploadResponse = await broadcastAPI.uploadFile(uploadFormData);
        if (uploadResponse.data.success) {
          contentToSend = uploadResponse.data.filePath;
        } else {
          throw new Error('文件上传失败');
        }
      }
      const broadcastData = {
        type: formData.type,
        content: contentToSend,
        displayMode: formData.displayMode
      };
      // Closing the operation or switching schools during an upload cancels
      // the pending broadcast; an already accepted send remains server-owned.
      if (!alive.current) return;
      if (formData.type !== 'text') {
        broadcastData.mediaUrl = contentToSend;
        broadcastData.metadata = { fileName: fileList[0]?.name, originalName: fileList[0]?.name };
      }
      if (formData.sendMode === 'class') {
        broadcastData.targetClass = formData.targetClass;
      } else if (formData.sendMode === 'grade') {
        broadcastData.targetGrade = formData.targetGrade;
        broadcastData.schoolId = String(targetSchool);
      } else {
        broadcastData.targetSchool = targetSchool;
      }
      await broadcastAPI.sendMessage(broadcastData);
      if (!alive.current) return;
      message.success('广播发送成功');
      setDrawerVisible(Boolean(workbench));
      setFormData({
        type: 'text',
        content: '',
        displayMode: 'banner',
        targetClass: null,
        targetSchool: workbench ? targetSchool : null,
        targetGrade: null,
        sendMode: workbench ? 'school' : 'class'
      });
      setFileList([]);
      fetchMessageHistory(1, historyPagination.pageSize, prepareFilterParams());
    } catch (error) {
      console.error('发送广播失败:', error);
      if (alive.current) message.error(error.response?.data?.message || '发送广播失败');
    } finally {
      sending.current = false;
      if (alive.current) setLoading(false);
    }
  };
  // ========== 定时广播相关方法 ==========
  // 获取定时广播列表
  const fetchScheduledBroadcasts = async () => {
    setLoading(true);
    try {
      const response = await broadcastAPI.getScheduledBroadcasts(workbench ? { schoolId: workbench.schoolId } : {});
      if (response.data.success) {
        setScheduledBroadcasts(response.data.data || []);
      }
    } catch (error) {
      console.error('获取定时广播列表失败:', error);
      message.error('获取定时广播列表失败');
    } finally {
      setLoading(false);
    }
  };
  // 处理定时广播表单字段变化
  const handleScheduledFormChange = (field, value) => {
    setScheduledFormData({ ...scheduledFormData, [field]: value });
  };
  // 创建定时广播
  const handleCreateScheduledBroadcast = async () => {
    try {
      // 验证必填字段
      if (!scheduledFormData.title) {
        message.error('请输入广播标题');
        return;
      }
      // 验证内容或文件
      if (scheduledFormData.broadcast_type === 'text') {
        if (!scheduledFormData.content) {
          message.error('请输入广播内容');
          return;
        }
      } else {
        if (scheduledFileList.length === 0) {
          message.error('请上传音频文件');
          return;
        }
      }
      if (!scheduledFormData.target_class_id) {
        message.error('请选择目标班级');
        return;
      }
      if (!scheduledFormData.schedule_time) {
        message.error('请选择执行时间');
        return;
      }
      // 根据定时类型验证额外字段
      if (
        scheduledFormData.schedule_type === 'weekly' &&
        normalizeWeekdayValues(scheduledFormData.schedule_weekday).length === 0
      ) {
        message.error('请选择星期几');
        return;
      }
      if (scheduledFormData.schedule_type === 'monthly' && !scheduledFormData.schedule_date) {
        message.error('请选择每月哪一天');
        return;
      }
      if (scheduledFormData.schedule_type === 'once' && !scheduledFormData.specific_date) {
        message.error('请选择执行日期');
        return;
      }
      setLoading(true);
      // 处理文件上传
      let contentToSubmit = scheduledFormData.content;
      if (scheduledFormData.broadcast_type === 'audio' && scheduledFileList.length > 0) {
        const uploadFormData = new FormData();
        uploadFormData.append('file', scheduledFileList[0].originFileObj);
        const uploadResponse = await broadcastAPI.uploadFile(uploadFormData);
        if (uploadResponse.data.success) {
          contentToSubmit = uploadResponse.data.filePath;
        } else {
          throw new Error('文件上传失败');
        }
      }
      // 准备提交数据
      const submitData = {
        title: scheduledFormData.title,
        content: contentToSubmit,
        broadcast_type: scheduledFormData.broadcast_type,
        display_mode: scheduledFormData.display_mode,
        target_class_id: scheduledFormData.target_class_id,
        schedule_type: scheduledFormData.schedule_type,
        schedule_time: scheduledFormData.schedule_time.format('HH:mm:ss'),
        schedule_weekday: normalizeWeekdayValues(scheduledFormData.schedule_weekday),
        schedule_date: scheduledFormData.schedule_date,
        specific_date: scheduledFormData.specific_date ? scheduledFormData.specific_date.format('YYYY-MM-DD') : null
      };
      const response = await broadcastAPI.createScheduledBroadcast(submitData);
      if (response.data.success) {
        message.success('定时广播创建成功');
        setScheduledDrawerVisible(false);
        // 重置表单
        setScheduledFormData({
          title: '',
          content: '',
          broadcast_type: 'text',
          display_mode: 'popup',
          target_class_id: null,
          schedule_type: 'weekly',
          schedule_time: null,
          schedule_weekday: [],
          schedule_date: null,
          specific_date: null
        });
        setScheduledFileList([]);
        // 刷新列表
        fetchScheduledBroadcasts();
      }
    } catch (error) {
      console.error('创建定时广播失败:', error);
      message.error(error.response?.data?.message || '创建定时广播失败');
    } finally {
      setLoading(false);
    }
  };
  // 暂停/恢复定时广播
  const handleToggleScheduled = async (id) => {
    try {
      const response = await broadcastAPI.toggleScheduledBroadcast(id);
      if (response.data.success) {
        message.success(response.data.message || '操作成功');
        fetchScheduledBroadcasts();
      }
    } catch (error) {
      console.error('切换定时广播状态失败:', error);
      message.error('操作失败');
    }
  };
  // 删除定时广播
  const handleDeleteScheduled = async (id) => {
    try {
      const response = await broadcastAPI.deleteScheduledBroadcast(id);
      if (response.data.success) {
        message.success('定时广播已删除');
        fetchScheduledBroadcasts();
      }
    } catch (error) {
      console.error('删除定时广播失败:', error);
      message.error('删除失败');
    }
  };
  // 查看执行日志
  const handleViewLogs = async (record) => {
    try {
      setCurrentSchedule(record);
      const response = await broadcastAPI.getScheduledBroadcastLogs(record.id);
      if (response.data.success) {
        setCurrentLogs(response.data.data || []);
        setLogsVisible(true);
      }
    } catch (error) {
      console.error('获取执行日志失败:', error);
      message.error('获取执行日志失败');
    }
  };
  // 处理定时广播行选择
  const onScheduledSelectChange = (newSelectedRowKeys) => {
    setSelectedScheduledKeys(newSelectedRowKeys);
  };
  // ========== 即时广播历史表格列定义 ==========
  const historyColumns = [
    {
      title: '消息ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
      width: 110,
      render: (type, record) => {
        let color = 'blue';
        let text = '文本';
        if (type === 'file') {
          color = 'green';
          text = '文件';
        } else if (type === 'image') {
          color = 'purple';
          text = '图像';
        }
        // 解析 metadata，找出是否是定时广播
        let metadata = null;
        if (record.metadata) {
          try {
            metadata = typeof record.metadata === 'string' ? JSON.parse(record.metadata) : record.metadata;
          } catch (e) {
            metadata = record.metadata;
          }
        }
        const isScheduled = metadata && (metadata.scheduledBroadcastId || metadata.scheduled_broadcast_id);
        const scheduleType = metadata && (metadata.scheduleType || metadata.schedule_type);
        return (
          <Space direction="vertical" size={4} style={{ display: 'flex' }}>
            <Tag color={color}>{text}</Tag>
            {isScheduled && (
              scheduleType === 'daily' ? <Tag color="orange">每日重复</Tag> :
              scheduleType === 'weekly' ? <Tag color="cyan">每周重复</Tag> :
              scheduleType === 'monthly' ? <Tag color="geekblue">每月重复</Tag> :
              scheduleType === 'once' ? <Tag color="gold">定时单次</Tag> :
              <Tag color="gold">定时广播</Tag>
            )}
          </Space>
        );
      }
    },
    {
      title: '内容',
      dataIndex: 'content',
      key: 'content',
      ellipsis: true,
      render: (content, record) => (
        <Button type="link" onClick={() => showMessageDetail(record)}>
          {content && content.length > 20 ? `${content.substring(0, 20)}...` : content || '查看详情'}
        </Button>
      )
    },
    {
      title: '目标',
      dataIndex: 'target_name',
      key: 'target_name',
      render: (_, record) => (
        <Space>
          {record.target_type === 'school' ? 
            <Tag color="purple">全校: {record.school_name}</Tag> : 
            <Tag color="blue">班级: {record.class_name}</Tag>
          }
        </Space>
      )
    },
    {
      title: '发送者',
      key: 'sender',
      render: (_, record) => (
        <Space>
          <Text>{record.sender_name}</Text>
          <Tag color={record.sender_type === 'admin' ? 'blue' : 'green'}>
            {record.sender_type === 'admin' ? '管理员' : '教师'}
          </Tag>
        </Space>
      )
    },
    {
      title: '发送时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text) => new Date(text).toLocaleString(),
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      render: (_, record) => {
        // 🔐 权限控制：判断是否可以删除
        // 1. 管理员可以删除所有消息
        // 2. 普通教师只能删除自己发送的消息
        const canDelete = isSuperAdmin() || isSchoolAdmin() || 
                         (user && record.sender_id === (user.teacherId || user.id) && record.sender_type === 'teacher');
        return (
          <Space size="small">
            <Button 
              type="link" 
              size="small"
              icon={<EyeOutlined />}
              onClick={() => showMessageDetail(record)}
            >
              查看
            </Button>
            {canDelete && (
              <Popconfirm
                title="确定删除这条消息吗？"
                onConfirm={() => handleDeleteMessage(record.id)}
                okText="确定"
                cancelText="取消"
              >
                <Button 
                  type="link" 
                  danger 
                  size="small"
                  icon={<DeleteOutlined />}
                >
                  删除
                </Button>
              </Popconfirm>
            )}
          </Space>
        );
      },
    },
  ];
  // 删除单条消息
  const handleDeleteMessage = async (id) => {
    try {
      await broadcastAPI.deleteMessage(id);
      message.success('消息已删除');
      fetchMessageHistory(1, historyPagination.pageSize, prepareFilterParams());
    } catch (error) {
      console.error('删除消息失败:', error);
      message.error('删除消息失败');
    }
  };
  // ========== 定时广播表格列定义 ==========
  const scheduledColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
    },
    {
      title: '标题',
      dataIndex: 'title',
      key: 'title',
      width: 200,
      ellipsis: true,
    },
    {
      title: '内容预览',
      dataIndex: 'content',
      key: 'content',
      ellipsis: true,
      render: (text) => (
        <Tooltip title={text}>
          {text && text.length > 30 ? `${text.substring(0, 30)}...` : text}
        </Tooltip>
      )
    },
    {
      title: '目标班级',
      dataIndex: 'class_name',
      key: 'class_name',
      width: 120,
      render: (text) => <Tag color="blue">{text}</Tag>
    },
    {
      title: '定时类型',
      dataIndex: 'schedule_type',
      key: 'schedule_type',
      width: 100,
      render: (type) => {
        const typeMap = {
          'once': { text: '一次性', color: 'orange' },
          'daily': { text: '每天', color: 'blue' },
          'weekly': { text: '每周', color: 'green' },
          'monthly': { text: '每月', color: 'purple' }
        };
        const config = typeMap[type] || { text: type, color: 'default' };
        return <Tag color={config.color}>{config.text}</Tag>;
      }
    },
    {
      title: '执行时间',
      key: 'schedule_info',
      width: 150,
      render: (_, record) => {
        let info = record.schedule_time;
        if (record.schedule_type === 'weekly') {
          const weekdayText = formatWeekdayText(record.schedule_weekday);
          info = weekdayText ? `${weekdayText} ${info}` : info;
        } else if (record.schedule_type === 'monthly') {
          info = `每月${record.schedule_date}日 ${info}`;
        } else if (record.schedule_type === 'once') {
          info = `${record.specific_date} ${info}`;
        }
        return <Text>{info}</Text>;
      }
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => {
        const statusMap = {
          'active': { text: '运行中', color: 'success' },
          'paused': { text: '已暂停', color: 'warning' },
          'expired': { text: '已过期', color: 'default' }
        };
        const config = statusMap[status] || { text: status, color: 'default' };
        return <Badge status={config.color} text={config.text} />;
      }
    },
    {
      title: '执行统计',
      key: 'stats',
      width: 150,
      render: (_, record) => (
        <Space direction="vertical" size="small">
          <Text type="secondary" style={{ fontSize: 12 }}>
            已执行: {record.execution_count || 0} 次
          </Text>
          {record.next_execute_at && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              下次: {moment(record.next_execute_at).format('MM-DD HH:mm')}
            </Text>
          )}
        </Space>
      )
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          {record.status === 'active' ? (
            <Tooltip title="暂停">
              <Button 
                type="link" 
                size="small"
                icon={<PauseCircleOutlined />}
                onClick={() => handleToggleScheduled(record.id)}
              >
                暂停
              </Button>
            </Tooltip>
          ) : record.status === 'paused' ? (
            <Tooltip title="恢复">
              <Button 
                type="link" 
                size="small"
                icon={<PlayCircleOutlined />}
                onClick={() => handleToggleScheduled(record.id)}
              >
                恢复
              </Button>
            </Tooltip>
          ) : null}
          <Tooltip title="查看日志">
            <Button 
              type="link" 
              size="small"
              icon={<HistoryOutlined />}
              onClick={() => handleViewLogs(record)}
            >
              日志
            </Button>
          </Tooltip>
          <Popconfirm
            title="确定删除这个定时广播吗？"
            onConfirm={() => handleDeleteScheduled(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button 
              type="link" 
              danger 
              size="small"
              icon={<DeleteOutlined />}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];
  // ========== 渲染即时广播历史Tab ==========
  const renderHistoryTab = () => (
    <Card>
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        {/* 工具栏 */}
        <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
          <Space wrap>
            <Button 
              type="primary" 
              icon={<SendOutlined />}
              onClick={() => setDrawerVisible(true)}
            >
              发送即时广播
            </Button>
            <Button 
              icon={<FilterOutlined />}
              onClick={() => setFilterDrawerVisible(true)}
            >
              高级筛选
            </Button>
            <Button 
              icon={<ReloadOutlined />}
              onClick={() => fetchMessageHistory(historyPagination.current, historyPagination.pageSize, prepareFilterParams())}
            >
              刷新
            </Button>
            {selectedRowKeys.length > 0 && (isSuperAdmin() || isSchoolAdmin()) && (
              <Popconfirm
                title={`确定删除选中的 ${selectedRowKeys.length} 条消息吗？`}
                onConfirm={handleBatchDelete}
                okText="确定"
                cancelText="取消"
              >
                <Button 
                  danger
                  icon={<DeleteOutlined />}
                  loading={deleteLoading}
                >
                  批量删除 ({selectedRowKeys.length})
                </Button>
              </Popconfirm>
            )}
          </Space>
          <Search
            placeholder="搜索广播内容..."
            allowClear
            enterButton={<SearchOutlined />}
            onSearch={handleQuickSearch}
            loading={searchLoading}
            style={{ width: 300 }}
          />
        </div>
        {/* 消息历史表格 */}
        <Table
          rowSelection={
            // 🔐 只有管理员才显示批量选择功能
            isSuperAdmin() || isSchoolAdmin() ? {
              selectedRowKeys,
              onChange: onSelectChange,
            } : undefined
          }
          columns={historyColumns}
          dataSource={messageHistory}
          rowKey="id"
          loading={loading}
          pagination={{
            ...historyPagination,
            showSizeChanger: true,
            pageSizeOptions: ['50', '100', '200', '500'],
            showTotal: (total) => `共 ${total} 条消息`,
            onChange: (page, pageSize) => {
              fetchMessageHistory(page, pageSize, prepareFilterParams());
            }
          }}
          scroll={{ x: 1200 }}
        />
      </Space>
    </Card>
  );
  // ========== 渲染定时广播Tab ==========
  const renderScheduledTab = () => (
    <Card>
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        {/* 工具栏 */}
        <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
          <Space wrap>
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              onClick={() => setScheduledDrawerVisible(true)}
            >
              创建定时广播
            </Button>
            <Button 
              icon={<ReloadOutlined />}
              onClick={fetchScheduledBroadcasts}
            >
              刷新
            </Button>
          </Space>
          <Space>
            <Tag color="blue" icon={<ClockCircleOutlined />}>
              共 {scheduledBroadcasts.length} 个定时任务
            </Tag>
            <Tag color="green">
              运行中: {scheduledBroadcasts.filter(s => s.status === 'active').length}
            </Tag>
            <Tag color="orange">
              已暂停: {scheduledBroadcasts.filter(s => s.status === 'paused').length}
            </Tag>
          </Space>
        </div>
        {/* 定时广播表格 */}
        <Table
          rowSelection={{
            selectedRowKeys: selectedScheduledKeys,
            onChange: onScheduledSelectChange,
          }}
          columns={scheduledColumns}
          dataSource={scheduledBroadcasts}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 个定时广播`,
          }}
          scroll={{ x: 1400 }}
        />
      </Space>
    </Card>
  );
  // ========== 渲染即时广播发送抽屉 ==========
  const renderSendDrawer = () => (
    <WorkbenchDrawer
      title="发送即时广播"
      width={600}
      open={drawerVisible}
      onClose={() => setDrawerVisible(false)}
      footer={
        <Space style={{ float: 'right' }}>
          <Button onClick={() => setDrawerVisible(false)}>取消</Button>
          <Button type="primary" onClick={handleSendBroadcast} loading={loading}>
            发送
          </Button>
        </Space>
      }
    >
      <Form layout="vertical">
        <Form.Item label="广播类型">
          <Radio.Group 
            onChange={(e) => handleFormChange('type', e.target.value)}
            value={formData.type}
          >
            <Radio value="text">文本消息</Radio>
            <Radio value="audio">音频文件</Radio>
            <Radio value="image">图片</Radio>
            <Radio value="file">文档／文件</Radio>
            <Radio value="video">视频</Radio>
          </Radio.Group>
        </Form.Item>
        <Form.Item label="发送模式">
          <Radio.Group
            onChange={(e) => handleFormChange('sendMode', e.target.value)}
            value={formData.sendMode}
          >
            <Radio value="class">指定班级</Radio>
            <Radio value="grade">按年级广播</Radio>
            <Radio value="school">全校广播</Radio>
          </Radio.Group>
        </Form.Item>
        {formData.sendMode === 'grade' && !workbench && (
          <Form.Item label="目标学校" required>
            <Select placeholder="选择学校" value={formData.targetSchool} onChange={(value) => setFormData((current) => ({ ...current, targetSchool: value, targetGrade: null }))}>
              {schools.map(school => <Option key={school.id} value={school.id}>{school.name}</Option>)}
            </Select>
          </Form.Item>
        )}
        {formData.sendMode === 'class' ? (
          <Form.Item label="目标班级" required>
            <Select
              placeholder="选择班级"
              value={formData.targetClass}
              onChange={(value) => handleFormChange('targetClass', value)}
              showSearch
              optionFilterProp="children"
            >
              {classes.map(cls => (
                <Option key={cls.id} value={cls.id}>{cls.name}</Option>
              ))}
            </Select>
          </Form.Item>
        ) : formData.sendMode === 'grade' ? (
          <Form.Item label="年级" required>
            <Select
              placeholder="选择年级"
              loading={gradesLoading}
              disabled={gradesLoading || !formData.targetSchool}
              value={formData.targetGrade}
              onChange={(value) => handleFormChange('targetGrade', value)}
            >
              {[...new Set(targetGrades)]
                .map(g => <Option key={g} value={g}>{g}</Option>)}
            </Select>
          </Form.Item>
        ) : !workbench && (
          <Form.Item label="目标学校" required>
            <Select
              placeholder="选择学校"
              value={formData.targetSchool}
              onChange={(value) => handleFormChange('targetSchool', value)}
              showSearch
              optionFilterProp="children"
            >
              {schools.map(school => (
                <Option key={school.id} value={school.id}>{school.name}</Option>
              ))}
            </Select>
          </Form.Item>
        )}
        <Form.Item label="显示模式">
          <Radio.Group 
            onChange={(e) => handleFormChange('displayMode', e.target.value)}
            value={formData.displayMode}
          >
            <Radio value="banner">横幅显示</Radio>
            <Radio value="popup">弹窗显示</Radio>
            <Radio value="fullscreen">全屏显示</Radio>
          </Radio.Group>
        </Form.Item>
        {formData.type === 'text' ? (
          <Form.Item label="广播内容" required>
            <TextArea 
              rows={6} 
              value={formData.content}
              onChange={(e) => handleFormChange('content', e.target.value)}
              placeholder="请输入广播内容"
              maxLength={500}
              showCount
            />
          </Form.Item>
        ) : (
          <Form.Item label="上传文件" required>
            <Upload
              beforeUpload={() => false}
              fileList={fileList}
              onChange={handleFileChange}
              maxCount={1}
            >
              <Button icon={<UploadOutlined />}>选择文件</Button>
            </Upload>
          </Form.Item>
        )}
      </Form>
    </WorkbenchDrawer>
  );
  // ========== 渲染定时广播创建抽屉 ==========
  const renderScheduledDrawer = () => (
    <Drawer
      title="创建定时广播"
      width={700}
      open={scheduledDrawerVisible}
      onClose={() => setScheduledDrawerVisible(false)}
      footer={
        <Space style={{ float: 'right' }}>
          <Button onClick={() => setScheduledDrawerVisible(false)}>取消</Button>
          <Button type="primary" onClick={handleCreateScheduledBroadcast} loading={loading}>
            创建
          </Button>
        </Space>
      }
    >
      <Form layout="vertical">
        <Form.Item label="广播标题" required tooltip="给这个定时广播起一个便于识别的名称">
          <Input
            placeholder="例如: 每周一早上提醒"
            value={scheduledFormData.title}
            onChange={(e) => handleScheduledFormChange('title', e.target.value)}
            maxLength={100}
            showCount
          />
        </Form.Item>
        {scheduledFormData.broadcast_type === 'text' ? (
          <Form.Item label="广播内容" required>
            <TextArea 
              rows={4} 
              value={scheduledFormData.content}
              onChange={(e) => handleScheduledFormChange('content', e.target.value)}
              placeholder="请输入广播内容"
              maxLength={500}
              showCount
            />
          </Form.Item>
        ) : (
          <Form.Item label="上传音频文件" required tooltip="支持MP3、WAV等音频格式">
            <Upload
              beforeUpload={() => false}
              fileList={scheduledFileList}
              onChange={({ fileList: newFileList }) => setScheduledFileList(newFileList)}
              maxCount={1}
              accept="audio/*"
            >
              <Button icon={<UploadOutlined />}>选择音频文件</Button>
            </Upload>
          </Form.Item>
        )}
        <Form.Item label="目标班级" required>
          <Select
            placeholder="选择班级"
            value={scheduledFormData.target_class_id}
            onChange={(value) => handleScheduledFormChange('target_class_id', value)}
            showSearch
            optionFilterProp="children"
          >
            {classes.map(cls => (
              <Option key={cls.id} value={cls.id}>{cls.name}</Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item label="广播类型">
          <Radio.Group 
            onChange={(e) => {
              handleScheduledFormChange('broadcast_type', e.target.value);
              // 切换类型时清空内容
              if (e.target.value === 'audio') {
                handleScheduledFormChange('content', '');
              } else {
                setScheduledFileList([]);
              }
            }}
            value={scheduledFormData.broadcast_type}
          >
            <Radio value="text">文本消息</Radio>
            <Radio value="audio">音频文件</Radio>
          </Radio.Group>
        </Form.Item>
        <Form.Item label="显示模式">
          <Radio.Group 
            onChange={(e) => handleScheduledFormChange('display_mode', e.target.value)}
            value={scheduledFormData.display_mode}
          >
            <Radio value="banner">横幅显示</Radio>
            <Radio value="popup">弹窗显示</Radio>
            <Radio value="fullscreen">全屏显示</Radio>
          </Radio.Group>
        </Form.Item>
        <Form.Item label="定时类型" required>
          <Radio.Group 
            onChange={(e) => handleScheduledFormChange('schedule_type', e.target.value)}
            value={scheduledFormData.schedule_type}
          >
            <Radio value="once">一次性</Radio>
            <Radio value="daily">每天</Radio>
            <Radio value="weekly">每周</Radio>
            <Radio value="monthly">每月</Radio>
          </Radio.Group>
        </Form.Item>
        {scheduledFormData.schedule_type === 'once' && (
          <Form.Item label="执行日期" required>
            <DatePicker
              style={{ width: '100%' }}
              value={scheduledFormData.specific_date}
              onChange={(date) => handleScheduledFormChange('specific_date', date)}
              disabledDate={(current) => current && current < moment().startOf('day')}
            />
          </Form.Item>
        )}
        {scheduledFormData.schedule_type === 'weekly' && (
          <Form.Item label="星期几" required>
            <Select
              mode="multiple"
              placeholder="选择星期几"
              value={normalizeWeekdayValues(scheduledFormData.schedule_weekday)}
              onChange={(value) => handleScheduledFormChange('schedule_weekday', normalizeWeekdayValues(value))}
            >
              {WEEKDAY_OPTIONS.map((item) => (
                <Option key={item.value} value={item.value}>{item.label}</Option>
              ))}
            </Select>
          </Form.Item>
        )}
        {scheduledFormData.schedule_type === 'monthly' && (
          <Form.Item label="每月哪一天" required>
            <InputNumber
              style={{ width: '100%' }}
              min={1}
              max={31}
              value={scheduledFormData.schedule_date}
              onChange={(value) => handleScheduledFormChange('schedule_date', value)}
              placeholder="1-31"
            />
          </Form.Item>
        )}
        <Form.Item label="执行时间" required>
          <TimePicker
            style={{ width: '100%' }}
            value={scheduledFormData.schedule_time}
            onChange={(time) => handleScheduledFormChange('schedule_time', time)}
            format="HH:mm"
          />
        </Form.Item>
        <Form.Item>
          <div style={{ 
            padding: '12px', 
            background: '#f0f2f5', 
            borderRadius: '4px',
            fontSize: '13px',
            color: '#666'
          }}>
            <CalendarOutlined style={{ marginRight: '8px' }} />
            <Text type="secondary">
              {scheduledFormData.schedule_type === 'once' && scheduledFormData.specific_date && scheduledFormData.schedule_time && 
                `将在 ${scheduledFormData.specific_date.format('YYYY-MM-DD')} ${scheduledFormData.schedule_time.format('HH:mm')} 执行一次`}
              {scheduledFormData.schedule_type === 'daily' && scheduledFormData.schedule_time && 
                `将在每天 ${scheduledFormData.schedule_time.format('HH:mm')} 执行`}
              {scheduledFormData.schedule_type === 'weekly' && normalizeWeekdayValues(scheduledFormData.schedule_weekday).length > 0 && scheduledFormData.schedule_time && 
                `将在每周${formatWeekdayText(scheduledFormData.schedule_weekday)} ${scheduledFormData.schedule_time.format('HH:mm')} 执行`}
              {scheduledFormData.schedule_type === 'monthly' && scheduledFormData.schedule_date && scheduledFormData.schedule_time && 
                `将在每月 ${scheduledFormData.schedule_date} 日 ${scheduledFormData.schedule_time.format('HH:mm')} 执行`}
              {!(scheduledFormData.schedule_time) && '请完善定时配置'}
            </Text>
          </div>
        </Form.Item>
      </Form>
    </Drawer>
  );
  // ========== 渲染过滤抽屉 ==========
  const renderFilterDrawer = () => (
    <Drawer
      title="高级筛选"
      width={400}
      open={filterDrawerVisible}
      onClose={() => setFilterDrawerVisible(false)}
      footer={
        <Space style={{ float: 'right' }}>
          <Button onClick={clearFilters} icon={<ClearOutlined />}>清空</Button>
          <Button type="primary" onClick={applyFilters}>应用筛选</Button>
        </Space>
      }
    >
      <Form layout="vertical">
        <Form.Item label="学校">
          <Select
            placeholder="选择学校"
            value={filters.schoolId}
            onChange={(value) => setFilters({ ...filters, schoolId: value })}
            allowClear
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
            }
          >
            {schools.map(school => (
              <Option key={school.id} value={school.id}>{school.name}</Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item label="班级">
          <Select
            placeholder="选择班级"
            value={filters.classId}
            onChange={(value) => setFilters({ ...filters, classId: value })}
            allowClear
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
            }
          >
            {classes.map(cls => (
              <Option key={cls.id} value={cls.id}>{cls.name}</Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item label="消息类型">
          <Select
            placeholder="选择类型"
            value={filters.type}
            onChange={(value) => setFilters({ ...filters, type: value })}
            allowClear
          >
            <Option value="text">文本</Option>
            <Option value="audio">音频</Option>
            <Option value="file">文件</Option>
            <Option value="image">图像</Option>
          </Select>
        </Form.Item>
        <Form.Item label="发送者">
          <Select
            placeholder="选择发送者"
            value={filters.senderId}
            onChange={(value) => setFilters({ ...filters, senderId: value })}
            allowClear
            showSearch
            optionFilterProp="children"
          >
            {senders.map(sender => (
              <Option key={sender.id} value={sender.id}>
                {sender.name} ({sender.type})
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item label="日期范围">
          <RangePicker
            style={{ width: '100%' }}
            value={filters.dateRange}
            onChange={(dates) => setFilters({ ...filters, dateRange: dates })}
          />
        </Form.Item>
        <Form.Item label="内容搜索">
          <Input
            placeholder="搜索消息内容"
            value={filters.contentQuery}
            onChange={(e) => setFilters({ ...filters, contentQuery: e.target.value })}
            allowClear
          />
        </Form.Item>
      </Form>
    </Drawer>
  );
  // ========== 渲染消息详情模态框 ==========
  const renderDetailModal = () => (
    <Modal
      title="广播详情"
      open={detailVisible}
      onCancel={() => setDetailVisible(false)}
      footer={[
        <Button key="close" onClick={() => setDetailVisible(false)}>
          关闭
        </Button>
      ]}
      width={700}
    >
      {currentMessage && (
        <Descriptions bordered column={1}>
          <Descriptions.Item label="消息ID">{currentMessage.id}</Descriptions.Item>
          <Descriptions.Item label="类型">
            <Tag color="blue">{currentMessage.type}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="内容">
            {currentMessage.type === 'text' ? (
              <div style={{ whiteSpace: 'pre-wrap' }}>{currentMessage.content}</div>
            ) : currentMessage.type === 'image' ? (
              <Image src={currentMessage.content} alt="广播图片" style={{ maxWidth: '100%' }} />
            ) : (
              <a href={currentMessage.content} target="_blank" rel="noopener noreferrer">
                {currentMessage.content}
              </a>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="目标">
            {currentMessage.target_type === 'school' ? 
              `全校: ${currentMessage.school_name}` : 
              `班级: ${currentMessage.class_name}`
            }
          </Descriptions.Item>
          <Descriptions.Item label="发送者">
            {currentMessage.sender_name} ({currentMessage.sender_type === 'admin' ? '管理员' : '教师'})
          </Descriptions.Item>
          <Descriptions.Item label="发送时间">
            {new Date(currentMessage.created_at).toLocaleString()}
          </Descriptions.Item>
          <Descriptions.Item label="显示模式">
            {currentMessage.display_mode === 'banner' ? '横幅' : 
             currentMessage.display_mode === 'popup' ? '弹窗' : '全屏'}
          </Descriptions.Item>
        </Descriptions>
      )}
    </Modal>
  );
  // ========== 渲染执行日志模态框 ==========
  const renderLogsModal = () => (
    <Modal
      title={`执行日志 - ${currentSchedule?.title}`}
      open={logsVisible}
      onCancel={() => setLogsVisible(false)}
      footer={[
        <Button key="close" onClick={() => setLogsVisible(false)}>
          关闭
        </Button>
      ]}
      width={900}
    >
      <Table
        columns={[
          {
            title: '执行时间',
            dataIndex: 'executed_at',
            key: 'executed_at',
            render: (text) => moment(text).format('YYYY-MM-DD HH:mm:ss')
          },
          {
            title: '状态',
            dataIndex: 'status',
            key: 'status',
            render: (status) => (
              <Badge 
                status={status === 'success' ? 'success' : 'error'} 
                text={status === 'success' ? '成功' : '失败'} 
              />
            )
          },
          {
            title: '消息ID',
            dataIndex: 'message_id',
            key: 'message_id',
          },
          {
            title: '错误信息',
            dataIndex: 'error_message',
            key: 'error_message',
            render: (text) => text ? <Text type="danger">{text}</Text> : '-'
          }
        ]}
        dataSource={currentLogs}
        rowKey="id"
        pagination={{ pageSize: 10 }}
        size="small"
      />
    </Modal>
  );
  const broadcastTabs = (
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane 
          tab={
            <span>
              <SendOutlined />
              广播记录
            </span>
          } 
          key="history"
        >
          {renderHistoryTab()}
        </TabPane>
        <TabPane 
          tab={
            <span>
              <ClockCircleOutlined />
              定时广播
              <Badge 
                count={scheduledBroadcasts.filter(s => s.status === 'active').length} 
                style={{ marginLeft: 8 }}
                showZero
              />
            </span>
          } 
          key="scheduled"
        >
          {renderScheduledTab()}
        </TabPane>
      </Tabs>
  );
  // ========== 主渲染 ==========
  return (
    <div style={{ padding: 24 }}>
      {!workbench && <Title level={2}>广播管理</Title>}
      {workbench && renderSendDrawer()}
      {workbench ? <Collapse items={[{ key: 'records', label: '查看广播记录与定时发送', children: broadcastTabs }]} /> : broadcastTabs}
      {/* 各种抽屉和模态框 */}
      {!workbench && renderSendDrawer()}
      {renderScheduledDrawer()}
      {renderFilterDrawer()}
      {renderDetailModal()}
      {renderLogsModal()}
    </div>
  );
};
export default BroadcastList;
